/**
 * Iso-surface extraction for the 3D viewer (docs/TODO.md 3.8). Pure (no three.js / DOM) so it
 * runs in the geo worker pool (task `isosurface`) and in Jest.
 *
 *  - marchingCubes(): classic 256-case marching cubes over a regular (or per-axis rectilinear)
 *    grid, shared vertices (indexed mesh), gradient normals pointing to increasing value
 *  - cellsToGrid(): inversion-model cells (x, y, depth, value) -> dense grid
 *  - sampleGrid(): trilinear sampling (used for the resampled slice on the cutting plane)
 *  - meshToObj() / meshToStl(): ASCII exporters
 */
import { EDGE_TABLE, TRI_TABLE } from './marching-cubes-tables';

// Corner offsets (see tables) and, per edge, the lower corner + axis it runs along.
const CORNER = [[0, 0, 0], [1, 0, 0], [1, 1, 0], [0, 1, 0], [0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]];
const EDGE_CORNERS = [[0, 1], [1, 2], [3, 2], [0, 3], [4, 5], [5, 6], [7, 6], [4, 7], [0, 4], [1, 5], [2, 6], [3, 7]];
const EDGE_LOW = EDGE_CORNERS.map(([a, b]) => {
  const ca = CORNER[a], cb = CORNER[b];
  const axis = ca[0] !== cb[0] ? 0 : ca[1] !== cb[1] ? 1 : 2;
  const low = ca[axis] < cb[axis] ? ca : cb;
  return { low, axis };
});
const TRIS = TRI_TABLE.map((row) => Array.from(row, (ch) => parseInt(ch, 16)));

function axisCoords(n, spacing, origin, given) {
  if (given && given.length === n) return Float64Array.from(given);
  const c = new Float64Array(n);
  for (let i = 0; i < n; i++) c[i] = origin + i * spacing;
  return c;
}

/**
 * @param {object} p
 * @param {ArrayLike<number>} p.values  nx*ny*nz, index = i + nx*(j + ny*k); NaN = no data (cell skipped)
 * @param {number[]} p.dims             [nx, ny, nz]
 * @param {number[]} [p.spacing]        [dx, dy, dz] (default 1)
 * @param {number[]} [p.origin]         [ox, oy, oz] (default 0)
 * @param {{x?:number[], y?:number[], z?:number[]}} [p.coords]  per-axis node coordinates (override spacing/origin)
 * @param {number} p.iso                iso level; nodes with value < iso are "inside"
 * @returns {{ positions: Float32Array, normals: Float32Array, indices: Uint32Array,
 *             vertexCount: number, triangleCount: number }}
 */
export function marchingCubes({ values, dims, spacing = [1, 1, 1], origin = [0, 0, 0], coords = {}, iso }) {
  const [nx, ny, nz] = dims;
  if (values.length < nx * ny * nz) throw new Error('marchingCubes: values shorter than dims');
  const cx = axisCoords(nx, spacing[0], origin[0], coords.x);
  const cy = axisCoords(ny, spacing[1], origin[1], coords.y);
  const cz = axisCoords(nz, spacing[2], origin[2], coords.z);
  const C = [cx, cy, cz];
  const sx = 1, sy = nx, sz = nx * ny;
  const idx = (i, j, k) => i + nx * (j + ny * k);

  // Gradient of the field at a node (central differences, one-sided at the boundary / near NaN)
  const grad = (i, j, k, out) => {
    const v = values[idx(i, j, k)];
    const d = (lo, hi, c, ci, stride, base) => {
      const a = lo ? values[base - stride] : NaN, b = hi ? values[base + stride] : NaN;
      const ca = ci > 0 ? c[ci - 1] : NaN, cb = ci < c.length - 1 ? c[ci + 1] : NaN;
      if (!Number.isNaN(a) && !Number.isNaN(b)) return (b - a) / (cb - ca);
      if (!Number.isNaN(b)) return (b - v) / (cb - c[ci]);
      if (!Number.isNaN(a)) return (v - a) / (c[ci] - ca);
      return 0;
    };
    const base = idx(i, j, k);
    out[0] = d(i > 0, i < nx - 1, cx, i, sx, base);
    out[1] = d(j > 0, j < ny - 1, cy, j, sy, base);
    out[2] = d(k > 0, k < nz - 1, cz, k, sz, base);
  };

  const positions = [];
  const normals = [];
  const indices = [];
  const cache = new Map();
  const v = new Float64Array(8);
  const g0 = [0, 0, 0], g1 = [0, 0, 0];

  const vertexOn = (e, i, j, k) => {
    const { low, axis } = EDGE_LOW[e];
    const li = i + low[0], lj = j + low[1], lk = k + low[2];
    const key = idx(li, lj, lk) * 3 + axis;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    const hi = [li + (axis === 0), lj + (axis === 1), lk + (axis === 2)];
    const v0 = values[idx(li, lj, lk)], v1 = values[idx(hi[0], hi[1], hi[2])];
    const t = v1 === v0 ? 0.5 : (iso - v0) / (v1 - v0);
    const pos = [cx[li], cy[lj], cz[lk]];
    pos[axis] = C[axis][[li, lj, lk][axis]] + t * (C[axis][hi[axis]] - C[axis][[li, lj, lk][axis]]);
    grad(li, lj, lk, g0);
    grad(hi[0], hi[1], hi[2], g1);
    let nxv = g0[0] + (g1[0] - g0[0]) * t, nyv = g0[1] + (g1[1] - g0[1]) * t, nzv = g0[2] + (g1[2] - g0[2]) * t;
    const len = Math.hypot(nxv, nyv, nzv);
    if (len > 0) { nxv /= len; nyv /= len; nzv /= len; } else { nxv = 0; nyv = 0; nzv = 1; }
    const n = positions.length / 3;
    positions.push(pos[0], pos[1], pos[2]);
    normals.push(nxv, nyv, nzv);
    cache.set(key, n);
    return n;
  };

  for (let k = 0; k < nz - 1; k++) {
    for (let j = 0; j < ny - 1; j++) {
      for (let i = 0; i < nx - 1; i++) {
        let ci = 0, bad = false;
        for (let c = 0; c < 8; c++) {
          const q = values[idx(i + CORNER[c][0], j + CORNER[c][1], k + CORNER[c][2])];
          if (Number.isNaN(q)) { bad = true; break; }
          v[c] = q;
          if (q < iso) ci |= 1 << c;
        }
        if (bad || EDGE_TABLE[ci] === 0) continue;
        const tri = TRIS[ci];
        for (let t = 0; t < tri.length; t += 3) {
          // Table order is clockwise seen from outside; reverse for outward (CCW) winding
          indices.push(vertexOn(tri[t + 2], i, j, k), vertexOn(tri[t + 1], i, j, k), vertexOn(tri[t], i, j, k));
        }
      }
    }
  }

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    indices: new Uint32Array(indices),
    vertexCount: positions.length / 3,
    triangleCount: indices.length / 3,
  };
}

/** ArrayBuffers of a mesh, for postMessage transfer. */
export const meshBuffers = (m) => [m.positions.buffer, m.normals.buffer, m.indices.buffer];

// ── Model cells -> grid ─────────────────────────────────────────────────────────────────────

const MAX_GRID_NODES = 12e6;
const uniqSorted = (arr) => [...new Set(arr.map((v) => Math.round(v * 1e6) / 1e6))].sort((a, b) => a - b);

/**
 * Dense grid from 3D inversion cells ({x, y, depth, [property]}). Missing cells are NaN.
 * `coords` holds the node coordinates (depth layers are usually non-uniform).
 * Returns null for models without a y coordinate.
 */
export function cellsToGrid(cells, property = 'resistivity', { log = property === 'resistivity' } = {}) {
  if (!cells || !cells.length || typeof cells[0].y !== 'number') return null;
  const xs = uniqSorted(cells.map((c) => c.x));
  const ys = uniqSorted(cells.map((c) => c.y));
  const zs = uniqSorted(cells.map((c) => c.depth));
  const [nx, ny, nz] = [xs.length, ys.length, zs.length];
  if (nx * ny * nz > MAX_GRID_NODES) throw new Error(`Grid too large for iso-surfaces (${nx}x${ny}x${nz})`);
  const xi = new Map(xs.map((v, i) => [v, i])), yi = new Map(ys.map((v, i) => [v, i])), zi = new Map(zs.map((v, i) => [v, i]));
  const values = new Float32Array(nx * ny * nz).fill(NaN);
  let min = Infinity, max = -Infinity;
  for (const c of cells) {
    const raw = c[property];
    if (typeof raw !== 'number' || (log && !(raw > 0))) continue;
    const val = log ? Math.log10(raw) : raw;
    const i = xi.get(Math.round(c.x * 1e6) / 1e6), j = yi.get(Math.round(c.y * 1e6) / 1e6), k = zi.get(Math.round(c.depth * 1e6) / 1e6);
    values[i + nx * (j + ny * k)] = val;
    if (val < min) min = val;
    if (val > max) max = val;
  }
  return { values, dims: [nx, ny, nz], coords: { x: xs, y: ys, z: zs }, range: [min, max], property, log };
}

function findInterval(c, v) {
  const n = c.length;
  if (v < c[0] || v > c[n - 1]) return -1;
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (c[mid] <= v) lo = mid; else hi = mid; }
  return lo;
}

/** Trilinear sample at model coordinates (x, y, depth); NaN outside the grid or on missing data. */
export function sampleGrid(grid, x, y, z) {
  const { values, dims: [nx, ny, nz], coords } = grid;
  if (nx < 2 || ny < 2 || nz < 2) return NaN;
  const i = findInterval(coords.x, x), j = findInterval(coords.y, y), k = findInterval(coords.z, z);
  if (i < 0 || j < 0 || k < 0) return NaN;
  const fx = (x - coords.x[i]) / ((coords.x[i + 1] - coords.x[i]) || 1);
  const fy = (y - coords.y[j]) / ((coords.y[j + 1] - coords.y[j]) || 1);
  const fz = (z - coords.z[k]) / ((coords.z[k + 1] - coords.z[k]) || 1);
  let acc = 0;
  for (let c = 0; c < 8; c++) {
    const [a, b, d] = CORNER[c];
    const q = values[(i + a) + nx * ((j + b) + ny * (k + d))];
    if (Number.isNaN(q)) return NaN;
    acc += q * (a ? fx : 1 - fx) * (b ? fy : 1 - fy) * (d ? fz : 1 - fz);
  }
  return acc;
}

// ── Exporters ───────────────────────────────────────────────────────────────────────────────

const f5 = (n) => String(Math.round(n * 1e5) / 1e5);

/** Wavefront OBJ (ASCII) with shared vertices and per-vertex normals. */
export function meshToObj({ positions, normals, indices }, name = 'isosurface') {
  const out = [`# Ultimate GIS iso-surface`, `o ${name}`];
  for (let i = 0; i < positions.length; i += 3) out.push(`v ${f5(positions[i])} ${f5(positions[i + 1])} ${f5(positions[i + 2])}`);
  const hasN = normals && normals.length === positions.length;
  if (hasN) for (let i = 0; i < normals.length; i += 3) out.push(`vn ${f5(normals[i])} ${f5(normals[i + 1])} ${f5(normals[i + 2])}`);
  for (let t = 0; t < indices.length; t += 3) {
    const [a, b, c] = [indices[t] + 1, indices[t + 1] + 1, indices[t + 2] + 1];
    out.push(hasN ? `f ${a}//${a} ${b}//${b} ${c}//${c}` : `f ${a} ${b} ${c}`);
  }
  return out.join('\n') + '\n';
}

/** ASCII STL; facet normals come from the triangle winding. */
export function meshToStl({ positions, indices }, name = 'isosurface') {
  const out = [`solid ${name}`];
  const p = (i) => [positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]];
  for (let t = 0; t < indices.length; t += 3) {
    const a = p(indices[t]), b = p(indices[t + 1]), c = p(indices[t + 2]);
    const u = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], w = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    let n = [u[1] * w[2] - u[2] * w[1], u[2] * w[0] - u[0] * w[2], u[0] * w[1] - u[1] * w[0]];
    const len = Math.hypot(n[0], n[1], n[2]) || 1;
    n = n.map((q) => q / len);
    out.push(`  facet normal ${f5(n[0])} ${f5(n[1])} ${f5(n[2])}`, '    outer loop');
    [a, b, c].forEach((q) => out.push(`      vertex ${f5(q[0])} ${f5(q[1])} ${f5(q[2])}`));
    out.push('    endloop', '  endfacet');
  }
  out.push(`endsolid ${name}`);
  return out.join('\n') + '\n';
}
